import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  FiAlertTriangle,
  FiGift,
  FiCheckCircle,
  FiArrowRight,
  FiHeart,
} from 'react-icons/fi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const CARBON_PLAN_OPTIONS = [
  {
    value: 'external_consultant',
    label: 'We hired an external sustainability consultant',
  },
  {
    value: 'going_with_someone_else',
    label: 'We are going with someone else',
  },
  {
    value: 'internal_spreadsheets',
    label: 'We are handling calculations internally using spreadsheets',
  },
];

// Steps: feedback → offer (conditional) → confirm → done(stayed|cancelled)
export default function CancelSubscriptionModal({
  show,
  onClose,
  onCancelled,
  onStayed,
  plan,
  retentionOfferUsed,
}) {
  const offerEligible = useMemo(
    () => plan !== 'enterprise' && !retentionOfferUsed,
    [plan, retentionOfferUsed],
  );

  const [step, setStep] = useState('feedback');
  const [carbonPlan, setCarbonPlan] = useState('');
  const [primaryReason, setPrimaryReason] = useState('');
  const [expectationGap, setExpectationGap] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!show) return null;

  const close = () => {
    if (submitting) return;
    setStep('feedback');
    setCarbonPlan('');
    setPrimaryReason('');
    setExpectationGap('');
    setError('');
    onClose();
  };

  const saveFeedback = async ({ offerShown, offerAccepted, cancelled }) => {
    // Only POST if the user actually filled anything in — otherwise skip the row entirely.
    const hasAny = carbonPlan || primaryReason.trim() || expectationGap.trim();
    if (!hasAny && !cancelled && !offerAccepted) return;
    try {
      await axios.post(`${API_BASE_URL}/billing/cancellation-feedback`, {
        carbon_management_plan: carbonPlan || null,
        primary_reason: primaryReason.trim() || null,
        expectation_gap: expectationGap.trim() || null,
        offer_shown: !!offerShown,
        offer_accepted: !!offerAccepted,
        cancelled: !!cancelled,
      });
    } catch (e) {
      // Don't block the cancellation flow on feedback failure — log only.
      // eslint-disable-next-line no-console
      console.warn('Failed to save cancellation feedback', e);
    }
  };

  const handleFeedbackNext = () => {
    setError('');
    if (offerEligible) {
      setStep('offer');
    } else {
      setStep('confirm');
    }
  };

  const handleAcceptOffer = async () => {
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/billing/retention-offer`, {});
      await saveFeedback({ offerShown: true, offerAccepted: true, cancelled: false });
      setStep('stayed');
      if (onStayed) onStayed();
    } catch (e) {
      setError(
        e?.response?.data?.error
          || e?.message
          || 'Could not apply the offer. Please contact support.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineOffer = () => {
    setError('');
    setStep('confirm');
  };

  const handleConfirmCancel = async () => {
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/billing/cancel-subscription`, {});
      await saveFeedback({
        offerShown: offerEligible,
        offerAccepted: false,
        cancelled: true,
      });
      setStep('cancelled');
      if (onCancelled) onCancelled();
    } catch (e) {
      setError(
        e?.response?.data?.error
          || e?.message
          || 'Could not cancel your subscription. Please try again or contact support.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = offerEligible ? 3 : 2;
  const stepIndex = step === 'feedback' ? 1 : step === 'offer' ? 2 : (offerEligible ? 3 : 2);
  const showProgress = step === 'feedback' || step === 'offer' || step === 'confirm';

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      tabIndex="-1"
      role="dialog"
    >
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        role="document"
        style={{ maxWidth: 560 }}
      >
        <div className="modal-content" style={{ borderRadius: 16, border: 'none', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
          {/* Header */}
          <div className="modal-header border-0 pb-0">
            <div className="d-flex flex-column">
              <h4 className="modal-title mb-0 fw-semibold">
                {step === 'feedback' && 'Before you go…'}
                {step === 'offer' && 'Wait — we\'d love to keep helping'}
                {step === 'confirm' && 'Confirm cancellation'}
                {step === 'stayed' && 'You\'re all set'}
                {step === 'cancelled' && 'Subscription cancelled'}
              </h4>
              {showProgress && (
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  Step {stepIndex} of {totalSteps}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={close}
              disabled={submitting}
              aria-label="Close"
            />
          </div>

          {/* Progress bar */}
          {showProgress && (
            <div className="px-4">
              <div className="progress" style={{ height: 4, borderRadius: 4 }}>
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${(stepIndex / totalSteps) * 100}%`, transition: 'width 200ms ease' }}
                />
              </div>
            </div>
          )}

          <div className="modal-body pt-3 pb-2 px-4">
            {error && (
              <div className="alert alert-danger" role="alert" style={{ fontSize: 14 }}>
                {error}
              </div>
            )}

            {/* ── Step 1: Feedback ── */}
            {step === 'feedback' && (
              <div>
                <p className="text-muted" style={{ fontSize: 14 }}>
                  Your answers help us make Zilch better. All questions are optional — skip any you don't want to answer.
                </p>

                <div className="mb-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 14 }}>
                    How are you planning to manage and verify your carbon accounting moving forward?
                  </label>
                  <div className="d-flex flex-column gap-2">
                    {CARBON_PLAN_OPTIONS.map((opt) => {
                      const checked = carbonPlan === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setCarbonPlan(checked ? '' : opt.value)}
                          className="text-start"
                          style={{
                            border: checked ? '1.5px solid #198754' : '1px solid #dee2e6',
                            background: checked ? '#f0f9f4' : '#fff',
                            borderRadius: 10,
                            padding: '12px 14px',
                            fontSize: 14,
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              border: checked ? '5px solid #198754' : '1.5px solid #adb5bd',
                              marginRight: 10,
                              verticalAlign: 'middle',
                              transition: 'all 150ms ease',
                            }}
                          />
                          <span style={{ verticalAlign: 'middle' }}>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 14 }}>
                    What was the primary reason you decided to stop using Zilch?
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={primaryReason}
                    onChange={(e) => setPrimaryReason(e.target.value)}
                    placeholder="Tell us in a sentence or two"
                    style={{ fontSize: 14, resize: 'vertical' }}
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 14 }}>
                    What was the biggest gap between what you expected Zilch to do and what actually happened in your account?
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={expectationGap}
                    onChange={(e) => setExpectationGap(e.target.value)}
                    placeholder="Anything we missed, broke, or got wrong"
                    style={{ fontSize: 14, resize: 'vertical' }}
                  />
                </div>
              </div>
            )}

            {/* ── Step 2: Retention offer ── */}
            {step === 'offer' && (
              <div className="text-center py-2">
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #d1f4dd 0%, #a8e6c1 100%)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <FiGift size={28} color="#198754" />
                </div>
                <h5 className="fw-semibold mb-2">2 months on us</h5>
                <p className="text-muted mb-3" style={{ fontSize: 14, lineHeight: 1.6 }}>
                  We'd hate to lose you. Stay on the <strong className="text-dark">{plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'current'} plan</strong> for two months free of charge.
                  After that, your normal billing resumes.
                </p>
                <ul
                  className="list-unstyled text-start mx-auto"
                  style={{ maxWidth: 360, fontSize: 14 }}
                >
                  <li className="mb-2 d-flex align-items-start gap-2">
                    <FiCheckCircle color="#198754" size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>No charge for the next 2 billing cycles</span>
                  </li>
                  <li className="mb-2 d-flex align-items-start gap-2">
                    <FiCheckCircle color="#198754" size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>Keep your full {plan || 'paid'}-plan access</span>
                  </li>
                  <li className="mb-2 d-flex align-items-start gap-2">
                    <FiCheckCircle color="#198754" size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>Cancel any time during the free period</span>
                  </li>
                </ul>
                <div className="text-muted mt-3 mb-0" style={{ fontSize: 11, lineHeight: 1.4 }}>
                  One-time offer per account. Accepting will replace any existing promotional discount on your subscription.
                </div>
              </div>
            )}

            {/* ── Step 3: Confirm cancel ── */}
            {step === 'confirm' && (
              <div>
                <div className="d-flex align-items-start gap-3 mb-3">
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: '#fff4e5',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FiAlertTriangle size={20} color="#d97706" />
                  </div>
                  <div>
                    <div className="fw-semibold mb-1">Your subscription will end immediately</div>
                    <div className="text-muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                      You'll be moved to the Free plan right away and won't be charged again. Your data stays in your account, and you can resubscribe at any time.
                    </div>
                  </div>
                </div>
                <div className="alert alert-light border mb-0" style={{ fontSize: 13 }}>
                  <strong>What you'll lose:</strong> paid-plan limits and features (assessments, PDF downloads, team seats, etc.) will revert to free-plan limits.
                </div>
              </div>
            )}

            {/* ── Outcome: Stayed ── */}
            {step === 'stayed' && (
              <div className="text-center py-3">
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <FiHeart size={32} color="#c2185b" />
                </div>
                <h5 className="fw-semibold mb-2">Thank you for staying</h5>
                <p className="text-muted mb-0" style={{ fontSize: 14, lineHeight: 1.6 }}>
                  Your next two billing cycles are on us. You'll see the credit applied on your next Stripe invoice, and regular billing resumes after that.
                </p>
              </div>
            )}

            {/* ── Outcome: Cancelled ── */}
            {step === 'cancelled' && (
              <div className="text-center py-3">
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: '#f8f9fa',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <FiCheckCircle size={32} color="#6c757d" />
                </div>
                <h5 className="fw-semibold mb-2">You're cancelled</h5>
                <p className="text-muted mb-0" style={{ fontSize: 14, lineHeight: 1.6 }}>
                  Your subscription has been cancelled and you won't be charged again. Your account has been moved to the Free plan. Thanks for giving Zilch a go — you can resubscribe whenever you're ready.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 pt-2 px-4 pb-4">
            {step === 'feedback' && (
              <>
                <button
                  type="button"
                  className="btn btn-link text-muted text-decoration-none"
                  onClick={handleFeedbackNext}
                  disabled={submitting}
                >
                  Skip
                </button>
                <button
                  type="button"
                  className="btn btn-dark d-inline-flex align-items-center gap-2"
                  onClick={handleFeedbackNext}
                  disabled={submitting}
                >
                  Continue <FiArrowRight size={16} />
                </button>
              </>
            )}

            {step === 'offer' && (
              <>
                <button
                  type="button"
                  className="btn btn-link text-muted text-decoration-none"
                  onClick={handleDeclineOffer}
                  disabled={submitting}
                >
                  No thanks, cancel anyway
                </button>
                <button
                  type="button"
                  className="btn btn-success d-inline-flex align-items-center gap-2"
                  onClick={handleAcceptOffer}
                  disabled={submitting}
                >
                  {submitting ? 'Applying…' : (
                    <>
                      Claim 2 months free <FiArrowRight size={16} />
                    </>
                  )}
                </button>
              </>
            )}

            {step === 'confirm' && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={close}
                  disabled={submitting}
                >
                  Keep my subscription
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmCancel}
                  disabled={submitting}
                >
                  {submitting ? 'Cancelling…' : 'Cancel subscription'}
                </button>
              </>
            )}

            {(step === 'stayed' || step === 'cancelled') && (
              <button
                type="button"
                className="btn btn-dark px-4"
                onClick={close}
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
